import type { ClientMessageData } from '@shared/api';
import { Flex, Tooltip } from 'antd';
import type { FC } from 'react';
import styles from './message-item.module.scss';
import { EditDetalized } from '@shared/assets/icons/components';
import { Text } from '@shared/ui/antd';
import { DateService } from '@shared/services/date';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { formatMessage } from './utils';

interface MessageItemProps {
  message: ClientMessageData;
  clientName?: string | null;
  clientAvatar?: string | null;
  agentNamesById?: Record<number, string>;
  switchNoticePrefix: string;
  switchNoticeTooltip: string;
  aiAgentLabel: string;
  onChange: (id: ClientMessageData) => void;
}

export const MessageItem: FC<MessageItemProps> = ({
  message,
  clientName,
  clientAvatar,
  agentNamesById,
  switchNoticePrefix,
  switchNoticeTooltip,
  aiAgentLabel /*onChange */,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'dialogs' });

  const isClientMessage = message?.creator_type === 'USER';
  const isAdminMessage = message?.creator_type === 'AI' || message?.creator_type === 'SYSTEM';
  const isManagerMessage = message?.creator_type === 'MANAGER';
  const switchedAgentName =
    typeof message?.switched_agent_id === 'number'
      ? (agentNamesById?.[message.switched_agent_id] ?? aiAgentLabel)
      : null;
  const switchNoticeText = switchedAgentName ? `${switchNoticePrefix} ${switchedAgentName}` : null;
  const formShownNoticeText = message?.is_form_shown ? t('chat.formShownNotice') : null;

  const formattedTime = (() => {
    if (!message.created_at) return '-';
    return DateService.format(message.created_at, 'HH:mm');
  })();

  const clientMessage = () => {
    return (
      <Flex className={styles.root} align="flex-end" gap={12}>
        <Flex className={styles.avatar__wrapper}>
          <div
            className={styles.avatar}
            style={{
              backgroundImage: clientAvatar ? `url(${clientAvatar})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
        </Flex>

        <div className={styles.message__wrapper}>
          <div className={classNames(styles.message__container, styles.message__container_client)}>
            <div className={styles.message__header}>
              <Text
                fontWeight={400}
                variant="sm"
                className={classNames(styles.message__title, styles.message__title_client)}
              >
                {clientName || t('chat.client')}
              </Text>

              <div className={styles.message__meta}>
                <Text variant="xs" fontWeight={400} className={styles.text__time}>
                  {formattedTime}
                </Text>
              </div>
            </div>

            <p
              className={styles.text}
              dangerouslySetInnerHTML={{ __html: formatMessage(message?.message || '') }}
            />
          </div>
        </div>
      </Flex>
    );
  };

  const adminMessage = () => {
    return (
      <Flex
        className={classNames(styles.root, styles.root_admin)}
        justify="flex-end"
        align="flex-end"
        gap={12}
      >
        <div className={styles.message__wrapper}>
          <div className={classNames(styles.message__container, styles.message__container_admin)}>
            <div className={styles.message__header}>
              <Text
                fontWeight={400}
                variant="sm"
                className={classNames(styles.message__title, styles.message__title_admin)}
              >
                {isAdminMessage ? t('chat.aiAgent') : t('chat.manager')}
              </Text>

              <div className={styles.message__meta}>
                <Text variant="xs" fontWeight={400} className={styles.text__time}>
                  {formattedTime}
                </Text>

                <EditDetalized />
              </div>
            </div>

            <p
              className={styles.text}
              dangerouslySetInnerHTML={{ __html: formatMessage(message?.message || '') }}
            />
          </div>
        </div>
      </Flex>
    );
  };

  return (
    <>
      {isClientMessage && clientMessage()}
      {(isAdminMessage || isManagerMessage) && adminMessage()}
      {isClientMessage && switchNoticeText && (
        <div className={styles.switchNoticeRow}>
          <Tooltip title={switchNoticeTooltip} placement="top">
            <Text variant="xs" className={styles.switchNoticeText}>
              {switchNoticeText}
            </Text>
          </Tooltip>
        </div>
      )}
      {formShownNoticeText && (
        <div className={styles.switchNoticeRow}>
          <Tooltip title={switchNoticeTooltip} placement="top">
            <Text variant="xs" className={styles.switchNoticeText}>
              {formShownNoticeText}
            </Text>
          </Tooltip>
        </div>
      )}
    </>
  );
};
